import React, { useEffect, useState, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { selectEmployees } from "src/stores/employeeSlice";
import moment from "moment";
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  useDraggable,
  useDroppable,
  closestCorners,
} from "@dnd-kit/core";
import {
  MessageSquare,
  Search,
  Filter,
  CheckCircle2,
  Clock,
  FileText,
  FileDown,
  Paperclip,
  X,
  Send,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  User,
  Check,
  Copy,
  Info,
  Calendar,
  Settings,
  Edit3,
  Eye,
  RotateCcw,
  LayoutGrid,
  List,
} from "lucide-react";
import {
  FormControl,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  Avatar,
  IconButton,
  Tooltip,
  InputAdornment,
  Typography,
  Box,
  Popover,
} from "@mui/material";
import CentralizedTable from "../components/CentralizedTable";
import UserAvatar from "../components/UserAvatar";
import apiClient from "../ApiClient";
import { notify } from "../utils/ToastUtils";
import TailwindModal from "../components/TailwindModal";
import LoaderIcon from "../components/LoaderIcon";
import { TICKET_CATEGORIES } from "../utils/constants";

// Droppable Kanban Column Container
function DroppableKanbanColumn({
  id,
  title,
  count,
  icon,
  topBorderColor,
  iconBg,
  iconColor,
  children,
}) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={`bg-white rounded-2xl border ${topBorderColor} transition-all duration-200 flex flex-col h-full min-h-[520px] shadow-2xs overflow-hidden ${
        isOver
          ? "border-blue-500 ring-2 ring-blue-400/40 shadow-md bg-blue-50/10"
          : "border-slate-200/90"
      }`}
    >
      {/* Header */}
      <div
        className="sticky top-0 z-10 bg-white px-4 py-3 flex items-center justify-between transition-colors border-b border-slate-100"
      >
        <div className="flex items-center gap-2.5">
          <div
            className={`w-7 h-7 rounded-lg ${iconBg} flex items-center justify-center ${iconColor} shrink-0`}
          >
            {icon}
          </div>
          <span className="text-xs sm:text-[13px] font-bold text-slate-800 tracking-tight">
            {title}
          </span>
          <span className="bg-slate-100 text-slate-600 text-[11px] font-bold px-2 py-0.5 rounded-full flex items-center justify-center min-w-[20px]">
            {count}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-3 space-y-3 flex-1 bg-slate-50/40 flex flex-col justify-start">
        {children}
      </div>
    </div>
  );
}

// Draggable Kanban Card Wrapper
function DraggableKanbanCard({ ticket, renderCard, onOpenTicket }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: ticket.REQUEST_ID,
    data: { ticket },
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={`transition-opacity duration-150 touch-none ${
        isDragging ? "opacity-25 scale-[0.98] pointer-events-none" : "opacity-100"
      }`}
    >
      {renderCard(ticket)}
    </div>
  );
}

export default function QueriesHR(props) {
  // View State: 'kanban' | 'list'
  const [viewMode, setViewMode] = useState("kanban");

  // Redux Employees for HR Team Filtering
  const employees = useSelector(selectEmployees);
  const hrTeam = useMemo(() => {
    return (
      employees
        ?.filter((emp) => emp?.DESIGNATION?.toLowerCase()?.includes("hr"))
        ?.map((emp) => ({
          empCode: emp.EMP_CODE,
          empId: emp.EMP_ID || emp.EMP_CODE,
          name: emp.EMP_NAME,
          email: emp.EMP_WORK_EMAIL || emp.EMP_PERSONAL_EMAIL || "",
          roleType: emp.DESIGNATION || "HR",
        })) || []
    );
  }, [employees]);

  // Data State
  const [tickets, setTickets] = useState([]);
  const categories = TICKET_CATEGORIES;
  const [loading, setLoading] = useState({ fetch: true, action: false });

  // Filters State
  const [search, setSearch] = useState("");
  const [selectedRaisedEmployees, setSelectedRaisedEmployees] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedPriority, setSelectedPriority] = useState("All");
  const [selectedAssignee, setSelectedAssignee] = useState("All");
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Date Range Popover State
  const [dateOption, setDateOption] = useState("all"); // 'all' | 'today' | 'this_week' | 'this_month' | 'last_month' | 'custom'
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [customRange, setCustomRange] = useState({
    startDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
  });
  const [datePopoverAnchor, setDatePopoverAnchor] = useState(null);
  const isDatePopoverOpen = Boolean(datePopoverAnchor);
  const [isCustomMode, setIsCustomMode] = useState(false);

  // Handle Preset Date Selection
  const handleSelectDateOption = (option) => {
    setDateOption(option);
    if (option === "all") {
      setDateRange({ startDate: null, endDate: null });
      setIsCustomMode(false);
      setDatePopoverAnchor(null);
    } else if (option === "today") {
      const today = moment().format("YYYY-MM-DD");
      setDateRange({ startDate: today, endDate: today });
      setIsCustomMode(false);
      setDatePopoverAnchor(null);
    } else if (option === "this_week") {
      const start = moment().startOf("week").format("YYYY-MM-DD");
      const end = moment().endOf("week").format("YYYY-MM-DD");
      setDateRange({ startDate: start, endDate: end });
      setIsCustomMode(false);
      setDatePopoverAnchor(null);
    } else if (option === "this_month") {
      const start = moment().startOf("month").format("YYYY-MM-DD");
      const end = moment().endOf("month").format("YYYY-MM-DD");
      setDateRange({ startDate: start, endDate: end });
      setIsCustomMode(false);
      setDatePopoverAnchor(null);
    } else if (option === "last_month") {
      const start = moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD");
      const end = moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD");
      setDateRange({ startDate: start, endDate: end });
      setIsCustomMode(false);
      setDatePopoverAnchor(null);
    } else if (option === "custom") {
      setIsCustomMode(true);
    }
  };

  // Handle Custom Date Range Done
  const handleCustomDateDone = () => {
    if (customRange.startDate && customRange.endDate) {
      if (moment(customRange.startDate).isAfter(moment(customRange.endDate))) {
        notify.warning("From date cannot be after To date");
        return;
      }
      setDateRange({
        startDate: customRange.startDate,
        endDate: customRange.endDate,
      });
      setDateOption("custom");
      setDatePopoverAnchor(null);
    } else {
      notify.warning("Please select both From and To dates");
    }
  };

  // Dynamic Date Filter Display Label
  const dateFilterLabel = useMemo(() => {
    if (dateOption === "all" || (!dateRange.startDate && !dateRange.endDate)) {
      return "All Dates";
    }
    if (dateOption === "today") return "Today";
    if (dateOption === "this_week") return "This Week";
    if (dateOption === "this_month") return "This Month";
    if (dateOption === "last_month") return "Last Month";
    if (dateRange.startDate && dateRange.endDate) {
      return `${moment(dateRange.startDate).format("DD MMM YYYY")} – ${moment(dateRange.endDate).format("DD MMM YYYY")}`;
    }
    return "All Dates";
  }, [dateOption, dateRange]);

  // Active Filters Count
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (search.trim()) count++;
    if (selectedRaisedEmployees.length > 0) count++;
    if (selectedCategory !== "All") count++;
    if (selectedStatus !== "All") count++;
    if (selectedPriority !== "All") count++;
    if (selectedAssignee !== "All") count++;
    if (dateOption !== "all" && (dateRange.startDate || dateRange.endDate)) count++;
    return count;
  }, [search, selectedRaisedEmployees, selectedCategory, selectedStatus, selectedPriority, selectedAssignee, dateOption, dateRange]);

  // Selected Ticket for Detail Drawer / Modal
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Modal Form State (MUI Controlled)
  const [modalPriority, setModalPriority] = useState("Medium");
  const [modalStatus, setModalStatus] = useState("In Progress");
  const [modalAssigneeCode, setModalAssigneeCode] = useState("");
  const [modalAssigneeName, setModalAssigneeName] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [modalAttachments, setModalAttachments] = useState([]);
  const [isCopied, setIsCopied] = useState(false);
  const [messageError, setMessageError] = useState(false);
  const [assigneeError, setAssigneeError] = useState(false);
  const fileInputRef = useRef(null);

  // Separate scroll references for messages timeline
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (isDetailModalOpen && selectedTicket) {
      const timer = setTimeout(() => {
        scrollToBottom();
      }, 120);
      return () => clearTimeout(timer);
    }
  }, [isDetailModalOpen, selectedTicket?.messages]);


  // Fetch Tickets List
  const fetchTickets = async (overrideFilters) => {
    setLoading((prev) => ({ ...prev, fetch: true }));
    try {
      const activeSearch = overrideFilters?.search !== undefined ? overrideFilters.search : search;
      const activeRaised = overrideFilters?.selectedRaisedEmployees !== undefined ? overrideFilters.selectedRaisedEmployees : selectedRaisedEmployees;
      const activeCategory = overrideFilters?.selectedCategory !== undefined ? overrideFilters.selectedCategory : selectedCategory;
      const activeStatus = overrideFilters?.selectedStatus !== undefined ? overrideFilters.selectedStatus : selectedStatus;
      const activePriority = overrideFilters?.selectedPriority !== undefined ? overrideFilters.selectedPriority : selectedPriority;
      const activeAssignee = overrideFilters?.selectedAssignee !== undefined ? overrideFilters.selectedAssignee : selectedAssignee;
      const activeDates = overrideFilters?.dateRange !== undefined ? overrideFilters.dateRange : dateRange;

      const params = {};
      if (activeSearch && activeSearch.trim()) params.search = activeSearch.trim();
      if (activeCategory !== "All") params.category = activeCategory;
      if (activeStatus !== "All") params.status = activeStatus;
      if (activePriority !== "All") params.priority = activePriority;
      if (activeAssignee !== "All") params.assignedTo = activeAssignee;

      const raisedCodes = (activeRaised || []).map((e) => e.EMP_CODE).filter(Boolean);
      if (raisedCodes.length > 0) {
        params.raisedBy = raisedCodes.join(",");
      }

      if (activeDates?.startDate && activeDates?.endDate) {
        params.startDate = moment(activeDates.startDate).format("YYYY-MM-DD");
        params.endDate = moment(activeDates.endDate).format("YYYY-MM-DD");
      }

      const res = await apiClient.get(`${process.env.REACT_APP_BASE_URL}/user/tickets/list`, { params });
      setTickets(res?.data || []);
    } catch (err) {
      console.error("Error fetching ticket list", err);
      notify.error("Failed to load support tickets");
    } finally {
      setLoading((prev) => ({ ...prev, fetch: false }));
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    fetchTickets();
  };

  const handleResetFilters = () => {
    setSearch("");
    setSelectedRaisedEmployees([]);
    setSelectedCategory("All");
    setSelectedStatus("All");
    setSelectedPriority("All");
    setSelectedAssignee("All");
    setDateOption("all");
    setDateRange({ startDate: null, endDate: null });
    setIsCustomMode(false);
    fetchTickets({
      search: "",
      selectedRaisedEmployees: [],
      selectedCategory: "All",
      selectedStatus: "All",
      selectedPriority: "All",
      selectedAssignee: "All",
      dateRange: { startDate: null, endDate: null },
    });
  };

  // Dnd-Kit Drag State and Sensors (6px distance activation constraint to allow normal clicking)
  const [activeTicket, setActiveTicket] = useState(null);
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 6,
    },
  });
  const sensors = useSensors(pointerSensor);

  const columnStatusMap = {
    new: "New",
    inProgress: "In Progress",
    waitingForEmployee: "Waiting for Employee",
    resolved: "Resolved",
  };

  // Open Ticket Details Modal (supports pre-selecting status from drag-drop)
  const handleOpenTicket = async (ticketId, overrideStatus = null, initialAssigneeError = false) => {
    try {
      const res = await apiClient.get(`${process.env.REACT_APP_BASE_URL}/user/tickets/${ticketId}`);
      const t = res?.data;
      setSelectedTicket(t || null);
      if (t) {
        setModalPriority(t.PRIORITY || "Medium");
        setModalStatus(overrideStatus || t.STATUS || "In Progress");
        setModalAssigneeCode(t.ASSIGNED_TO_CODE || "");
        setModalAssigneeName(t.ASSIGNED_TO_NAME || "");
        setModalMessage("");
        setModalAttachments([]);
        setMessageError(false);
        setAssigneeError(initialAssigneeError);
      }
      setIsDetailModalOpen(true);
    } catch (err) {
      console.error("Error fetching ticket details", err);
      notify.error("Failed to open ticket details");
    }
  };

  // Direct Status Update Handler (for instant drag moves to New or In Progress)
  const handleDirectStatusChange = async (ticketId, newStatus) => {
    const ticket = tickets.find((t) => t.REQUEST_ID === ticketId);
    if (!ticket || ticket.STATUS === newStatus) return;

    // Optimistic UI update
    setTickets((prev) =>
      prev.map((t) => (t.REQUEST_ID === ticketId ? { ...t, STATUS: newStatus } : t))
    );

    try {
      await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/user/tickets/${ticketId}/update`,
        {
          priority: ticket.PRIORITY,
          status: newStatus,
          assignedToCode: ticket.ASSIGNED_TO_CODE,
          assignedToName: ticket.ASSIGNED_TO_NAME,
          message: "",
          attachments: [],
        }
      );
      notify.success(`Ticket #${ticketId} moved to ${newStatus}`);
      if (props.checkQueries) props.checkQueries();
    } catch (err) {
      console.error("Error moving ticket", err);
      notify.error(err?.response?.data?.message || "Failed to update ticket status");
      fetchTickets();
    }
  };

  // Dnd-Kit Drag Event Handlers
  const handleDragStart = (event) => {
    const { active } = event;
    const ticket = active.data.current?.ticket || tickets.find((t) => t.REQUEST_ID === active.id);
    setActiveTicket(ticket || null);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveTicket(null);

    if (!over) return;

    const ticketId = active.id;
    const targetColumn = over.id;
    const targetStatus = columnStatusMap[targetColumn];

    if (!targetStatus) return;

    const currentTicket = tickets.find((t) => t.REQUEST_ID === ticketId);
    if (!currentTicket) return;

    // Normalize check
    const currentStatusNormalized = currentTicket.STATUS === "Pending" ? "New" : currentTicket.STATUS;
    if (currentStatusNormalized === targetStatus) return;

    // Gated Check: If ticket has NO assignee, dragging to ANY section opens modal with assignee error
    const hasAssignee = Boolean(currentTicket.ASSIGNED_TO_CODE && currentTicket.ASSIGNED_TO_CODE.trim());
    if (!hasAssignee) {
      handleOpenTicket(ticketId, targetStatus, true);
      return;
    }

    // Gated Status Check: "Waiting for Employee" or "Resolved" strictly require a note
    if (targetStatus === "Waiting for Employee" || targetStatus === "Resolved") {
      handleOpenTicket(ticketId, targetStatus, false);
      return;
    }

    // Direct Status Update for "New" or "In Progress"
    handleDirectStatusChange(ticketId, targetStatus);
  };

  // Copy Ticket ID to Clipboard
  const handleCopyTicketId = () => {
    if (!selectedTicket?.REQUEST_ID) return;
    navigator.clipboard.writeText(selectedTicket.REQUEST_ID);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };


  // File Upload Handler for Staged Attachments (Uploads to native storage via Multer)
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        notify.warning(`File "${file.name}" exceeds 10MB limit`);
        return;
      }
    }

    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const res = await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/user/tickets/upload`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      const uploadedFiles = res?.data?.files || [];
      setModalAttachments((prev) => [...prev, ...uploadedFiles]);
    } catch (err) {
      console.error("Error uploading ticket files", err);
      notify.error("Failed to upload file(s)");
    } finally {
      e.target.value = null;
    }
  };

  // Download Attachment Helper
  const handleDownloadAttachment = async (file, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      const fileName = file.name || file.filename || "attachment";
      if (file.url) {
        let fullUrl = file.url;
        if (!fullUrl.startsWith("http://") && !fullUrl.startsWith("https://")) {
          const publicBase =
            process.env.REACT_APP_PUBLIC_URL ||
            (process.env.REACT_APP_BASE_URL
              ? process.env.REACT_APP_BASE_URL.replace(/\/api\/?$/, "")
              : "");
          fullUrl = `${publicBase}${fullUrl.startsWith("/") ? "" : "/"}${fullUrl}`;
        }
        const response = await fetch(fullUrl);
        if (!response.ok) throw new Error("File could not be downloaded from server");
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } else if (file.data) {
        const dataUrl = file.data.startsWith("data:")
          ? file.data
          : `data:${file.type || "application/octet-stream"};base64,${file.data}`;
        const response = await fetch(dataUrl);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } else {
        notify.warning("No file URL or data found");
      }
    } catch (err) {
      console.error("Failed to download attachment", err);
      notify.error("Failed to download file");
    }
  };

  // Dynamic Validation Check (Only required when transitioning to 'Waiting for Employee' or 'Resolved')
  const isMessageRequired =
    (modalStatus === "Waiting for Employee" && selectedTicket?.STATUS !== "Waiting for Employee") ||
    (modalStatus === "Resolved" && selectedTicket?.STATUS !== "Resolved");

  // UNIFIED SEND HANDLER (Single Atomic API Call)
  const handleSendUnified = async () => {
    // Validation 1: Assignee is strictly required (Shown in Modal)
    if (!modalAssigneeCode || !modalAssigneeCode.trim()) {
      setAssigneeError(true);
      return;
    }

    // Validation 2: Require message only when status is "Waiting for Employee" or "Resolved"
    if (isMessageRequired && !modalMessage.trim() && modalAttachments.length === 0) {
      setMessageError(true);
      return;
    }

    setLoading((prev) => ({ ...prev, action: true }));
    try {
      const payload = {
        priority: modalPriority,
        status: modalStatus,
        assignedToCode: modalAssigneeCode,
        assignedToName: modalAssigneeName,
        message: modalMessage.trim(),
        attachments: modalAttachments,
      };

      await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/user/tickets/${selectedTicket.REQUEST_ID}/update`,
        payload
      );

      notify.success("Ticket updated successfully!");
      setModalMessage("");
      setModalAttachments([]);
      setMessageError(false);
      setAssigneeError(false);
      setIsDetailModalOpen(false);
      setSelectedTicket(null);
      fetchTickets();
      if (props.checkQueries) props.checkQueries();
    } catch (err) {
      console.error("Error updating ticket", err);
      notify.error(err?.response?.data?.message || "Failed to update ticket");
    } finally {
      setLoading((prev) => ({ ...prev, action: false }));
    }
  };

  // Group tickets into 4 active Kanban columns
  const kanbanColumns = useMemo(() => {
    return {
      new: tickets.filter((t) => t.STATUS === "New" || t.STATUS === "Pending"),
      inProgress: tickets.filter((t) => t.STATUS === "In Progress"),
      waitingForEmployee: tickets.filter((t) => t.STATUS === "Waiting for Employee"),
      resolved: tickets.filter((t) => t.STATUS === "Resolved"),
    };
  }, [tickets]);

  // Priority Badge Renderer
  const renderPriorityBadge = (p) => {
    if (p === "High") {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-[#fef2f2] text-[#e11d48]">
          <span className="w-2 h-2 rounded-full bg-[#e11d48] shrink-0" />
          High
        </span>
      );
    }
    if (p === "Medium") {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-[#fff7ed] text-[#c2410c]">
          <span className="w-2 h-2 rounded-full bg-[#ea580c] shrink-0" />
          Medium
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-[#eef4ff] text-[#2563eb]">
        <span className="w-2 h-2 rounded-full bg-[#2563eb] shrink-0" />
        Low
      </span>
    );
  };

  // Status Badge Renderer
  const renderStatusBadge = (status) => {
    switch (status) {
      case "New":
      case "Pending":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-[#F1F5F9] text-[#64748B]">
            <span className="w-2 h-2 rounded-full bg-[#64748B] shrink-0" />
            New
          </span>
        );
      case "In Progress":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-[#EFF6FF] text-[#2563EB]">
            <span className="w-2 h-2 rounded-full bg-[#2563EB] shrink-0" />
            In Progress
          </span>
        );
      case "Waiting for Employee":
      case "Waiting on you":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-[#FEFCE8] text-[#A16207]">
            <span className="w-2 h-2 rounded-full bg-[#CA8A04] shrink-0" />
            Waiting on You
          </span>
        );
      case "Resolved":
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-[#ECFDF5] text-[#059669]">
            <span className="w-2 h-2 rounded-full bg-[#059669] shrink-0" />
            Resolved
          </span>
        );
      case "Closed":
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium bg-[#F8FAFC] text-[#94A3B8] border border-slate-200/80">
            <span className="w-2 h-2 rounded-full bg-[#94A3B8] shrink-0" />
            Closed
          </span>
        );
    }
  };

  // Table Columns Definition for List View
  const columns = [
    {
      headerName: "Ticket ID",
      field: "REQUEST_ID",
      width: 140,
      renderCell: (params) => {
        const ticketId = params?.row?.REQUEST_ID ? `#${params.row.REQUEST_ID}` : "-";
        return (
          <Tooltip title={ticketId} arrow placement="top">
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                whiteSpace: "normal",
                lineHeight: 1.4,
                cursor: "pointer",
                fontFamily: "monospace",
                fontSize: "0.75rem",
                fontWeight: 700,
                color: "#2563eb",
                bgcolor: "#eff6ff",
                border: "1px solid #dbeafe",
                px: 1,
                py: 0.25,
                borderRadius: "6px",
                textAlign: "center",
              }}
            >
              {ticketId}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      headerName: "Employee",
      field: "RAISED_BY",
      width: 190,
      renderCell: (params) => {
        const empName = params?.row?.RAISED_BY || "-";
        const empId = params?.row?.EMP_ID || params?.row?.EMP_CODE || "";
        const fullTooltip = `${empName} (${empId})`;
        return (
          <div className="flex items-center gap-3 w-full">
            <UserAvatar name={empName} size={32} />
            <Tooltip title={fullTooltip} arrow placement="top">
              <div className="flex flex-col justify-center h-full w-full overflow-hidden">
                <Typography
                  variant="body2"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: "vertical",
                    whiteSpace: "normal",
                    lineHeight: 1.3,
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    color: "#1e293b",
                    cursor: "pointer",
                  }}
                >
                  {empName}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: "vertical",
                    whiteSpace: "normal",
                    lineHeight: 1.2,
                    fontSize: "0.6875rem",
                    fontWeight: 500,
                    marginTop:"3px",
                    color: "#94a3b8",
                  }}
                >
                  {empId}
                </Typography>
              </div>
            </Tooltip>
          </div>
        );
      },
    },
    {
      headerName: "Subject",
      field: "SUBJECT",
      width: 260,
      renderCell: (params) => {
        const subjectText = params?.row?.SUBJECT || "Support Query";
        return (
          <Tooltip title={subjectText} arrow placement="top">
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                whiteSpace: "normal",
                lineHeight: 1.4,
                fontSize: "0.75rem",
                fontWeight: 600,
                color: "#1e293b",
                cursor: "pointer",
                width: "100%",
              }}
            >
              {subjectText}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      headerName: "Category",
      field: "CATEGORY",
      width: 130,
      renderCell: (params) => {
        const categoryText = params?.row?.CATEGORY || "General";
        return (
          <Tooltip title={categoryText} arrow placement="top">
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                whiteSpace: "normal",
                lineHeight: 1.4,
                fontSize: "0.75rem",
                fontWeight: 500,
                color: "#334155",
                cursor: "pointer",
                width: "100%",
              }}
            >
              {categoryText}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      headerName: "Priority",
      field: "PRIORITY",
      width: 110,
      renderCell: (params) => (
        <Tooltip title={`Priority: ${params?.row?.PRIORITY || "Low"}`} arrow placement="top">
          <div className="truncate max-w-full">
            {renderPriorityBadge(params?.row?.PRIORITY)}
          </div>
        </Tooltip>
      ),
    },
    {
      headerName: "Status",
      field: "STATUS",
      width: 160,
      renderCell: (params) => (
        <Tooltip title={`Status: ${params?.row?.STATUS || "New"}`} arrow placement="top">
          <div className="truncate max-w-full">
            {renderStatusBadge(params?.row?.STATUS)}
          </div>
        </Tooltip>
      ),
    },
    {
      headerName: "Assignee",
      field: "ASSIGNED_TO_NAME",
      width: 160,
      renderCell: (params) => {
        const assigneeText = params?.row?.ASSIGNED_TO_NAME || "Unassigned";
        return (
          <Tooltip title={`Assigned: ${assigneeText}`} arrow placement="top">
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                whiteSpace: "normal",
                fontSize: "0.75rem",
                fontWeight: 500,
                color: params?.row?.ASSIGNED_TO_NAME ? "#334155" : "#94a3b8",
                fontStyle: params?.row?.ASSIGNED_TO_NAME ? "normal" : "italic",
                width: "100%",
                cursor: "pointer",
              }}
            >
              {assigneeText}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      headerName: "Raised Date",
      field: "RAISED_DATE",
      width: 130,
      renderCell: (params) => {
        const fullDateStr = moment(params?.row?.RAISED_DATE).format("DD MMM YYYY, hh:mm:ss A");
        return (
          <Tooltip title={fullDateStr} arrow placement="top">
            <div className="flex flex-col justify-center h-full w-full overflow-hidden">
              <Typography
                variant="body2"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: "#334155",
                }}
              >
                {moment(params?.row?.RAISED_DATE).format("DD MMM YYYY")}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: "0.625rem",
                  fontWeight: 500,
                  color: "#94a3b8",
                }}
              >
                {moment(params?.row?.RAISED_DATE).format("hh:mm A")}
              </Typography>
            </div>
          </Tooltip>
        );
      },
    },
    {
      headerName: "Action",
      field: "actions",
      width: 140,
      sortable: false,
      renderCell: (params) => {
        const isClosed = params?.row?.STATUS === "Closed";
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <Tooltip title={isClosed ? "View Ticket Details" : "Edit & Update Ticket"} arrow placement="top">
              <button
                type="button"
                onClick={() => handleOpenTicket(params?.row?.REQUEST_ID)}
                disabled={loading.fetch || loading.action}
                className={`flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors whitespace-nowrap shadow-2xs ${
                  isClosed
                    ? "border border-slate-300 text-slate-700 bg-slate-50 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    : "border border-blue-600 text-blue-600 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                }`}
              >
                {isClosed ? (
                  <>
                    <Eye size={13} className="shrink-0 text-slate-500" />
                    <span>Open Ticket</span>
                  </>
                ) : (
                  <>
                    <Edit3 size={13} className="shrink-0" />
                    <span>Edit Ticket</span>
                  </>
                )}
              </button>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  // Selected Assignee Info Helper
  const currentSelectedAssignee = useMemo(() => {
    if (!modalAssigneeCode) return null;
    return hrTeam.find((h) => h.empCode === modalAssigneeCode);
  }, [modalAssigneeCode, hrTeam]);

  // Check if anything has been modified in the modal
  const hasFormChanges = useMemo(() => {
    if (!selectedTicket) return false;
    const initialPriority = selectedTicket.PRIORITY || "Medium";
    const initialStatus = selectedTicket.STATUS === "Pending" ? "New" : (selectedTicket.STATUS || "New");
    const initialAssignee = selectedTicket.ASSIGNED_TO_CODE || "";

    const isPriorityChanged = modalPriority !== initialPriority;
    const isStatusChanged = modalStatus !== initialStatus;
    const isAssigneeChanged = (modalAssigneeCode || "") !== initialAssignee;
    const hasMessage = modalMessage.trim().length > 0;
    const hasAttachments = modalAttachments.length > 0;

    return isPriorityChanged || isStatusChanged || isAssigneeChanged || hasMessage || hasAttachments;
  }, [
    selectedTicket,
    modalPriority,
    modalStatus,
    modalAssigneeCode,
    modalMessage,
    modalAttachments,
  ]);

  // Estimated Auto-Close Date for Resolution Preview
  const estimatedAutoCloseDate = useMemo(() => {
    if (selectedTicket?.AUTO_CLOSE_DATE && selectedTicket.STATUS === "Resolved") {
      return moment(selectedTicket.AUTO_CLOSE_DATE).format("DD MMM YYYY");
    }
    // Calculate 2 business days from today skipping Saturday (6) & Sunday (0)
    let count = 0;
    let current = moment();
    while (count < 2) {
      current.add(1, "day");
      const d = current.day();
      if (d !== 0 && d !== 6) {
        count++;
      }
    }
    return current.format("DD MMM YYYY");
  }, [selectedTicket]);

  // Kanban Card Renderer
  const renderKanbanCard = (ticket, isOverlay = false) => {
    const raisedByName = ticket.RAISED_BY || "Employee";
    const initial = (ticket.RAISED_BY || "E").trim().charAt(0).toUpperCase();

    // Priority badge styling
    const getCardPriorityBadge = (p) => {
      if (p === "High") {
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-xs font-semibold bg-[#fef2f2] text-[#e11d48]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#e11d48] shrink-0" /> High
          </span>
        );
      }
      if (p === "Medium") {
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-xs font-semibold bg-[#fff7ed] text-[#c2410c]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#ea580c] shrink-0" /> Medium
          </span>
        );
      }
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-xs font-semibold bg-[#eef4ff] text-[#2563eb]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#2563eb] shrink-0" /> Low
        </span>
      );
    };

    const snippet =
      ticket.first_message ||
      ticket.FIRST_MESSAGE ||
      ticket.DESCRIPTION ||
      ticket.MESSAGE ||
      ticket.last_message ||
      ticket.LATEST_MESSAGE ||
      (ticket.messages && ticket.messages.length > 0 ? ticket.messages[0]?.MESSAGE : "") ||
      ticket.SUBJECT ||
      "";

    const messageCount =
      ticket.message_count ||
      ticket.MESSAGES_COUNT ||
      ticket.MESSAGE_COUNT ||
      (ticket.messages ? ticket.messages.length : (ticket.TOTAL_MESSAGES || 1));

    return (
      <div
        key={ticket.REQUEST_ID}
        onClick={() => !isOverlay && handleOpenTicket(ticket.REQUEST_ID)}
        className={`bg-white rounded-2xl border p-3.5 transition-all space-y-2.5 group ${
          isOverlay
            ? "border-blue-400 shadow-[0_22px_45px_rgba(15,23,42,0.18)] rotate-[1.5deg] scale-[1.03] cursor-grabbing ring-2 ring-blue-500/50 pointer-events-none"
            : "border-slate-200/80 shadow-[0_2px_8px_rgba(15,23,42,0.06)] hover:shadow-[0_4px_16px_rgba(15,23,42,0.1)] hover:border-blue-300 cursor-grab active:cursor-grabbing"
        }`}
      >
        {/* Top row: Blue Ticket ID Pill + Priority */}
        <div className="flex items-center justify-between gap-2">
          <span
            className="text-[10px] font-semibold text-[#2563eb] bg-[#eff6ff] px-2 py-0.5 rounded-md truncate max-w-[155px]"
            title={ticket.REQUEST_ID}
          >
            #{ticket.REQUEST_ID}
          </span>
          <div className="shrink-0">
            {getCardPriorityBadge(ticket.PRIORITY)}
          </div>
        </div>

        {/* Subject Title */}
        <h4 className="text-xs sm:text-[13px] font-bold text-slate-900 leading-snug group-hover:text-blue-600 transition-colors line-clamp-1">
          {ticket.SUBJECT || "Support Query"}
        </h4>

        {/* Issue First Message Snippet */}
        {snippet && (
          <p className="text-[11px] text-slate-500 line-clamp-2 leading-relaxed font-normal">
            {snippet}
          </p>
        )}

        {/* Requester Avatar & Name (Status badge removed) */}
        <div className="flex items-center gap-2 min-w-0 pt-0.5">
          <div className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[10px] font-bold shrink-0">
            {initial}
          </div>
          <span className="text-xs font-bold text-slate-800 uppercase tracking-tight truncate max-w-[200px]" title={raisedByName}>
            {raisedByName}
          </span>
        </div>

        {/* Assigned To */}
        <div className="flex items-center justify-between text-xs pt-1.5 border-t border-slate-100 gap-2">
          <span className="text-slate-500 font-normal shrink-0">Assignee</span>
          <span
            className={`text-xs truncate max-w-[150px] text-right pr-1 ${
              ticket.ASSIGNED_TO_NAME
                ? "text-slate-800 font-bold uppercase"
                : "text-slate-400 italic font-normal"
            }`}
            title={ticket.ASSIGNED_TO_NAME || "Unassigned"}
          >
            {ticket.ASSIGNED_TO_NAME || "Unassigned"}
          </span>
        </div>

        {/* Footer: Date & Message Count */}
        <div className="flex items-center justify-between text-xs text-slate-400 font-normal pt-1.5 border-t border-slate-100">
          <div className="flex items-center gap-1.5">
            <Calendar size={13} className="text-slate-400" />
            <span className="text-slate-500 text-[11px]">{moment(ticket.RAISED_DATE || ticket.CREATED_DATE).format("DD MMM YYYY, hh:mm A")}</span>
          </div>
          <div className="flex items-center gap-1">
            <MessageSquare size={13} className="text-slate-400" />
            <span className="text-slate-500 text-[11px]">{messageCount}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 pb-6">

      {/* Top Navigation Bar: Left Tabs (Ticket Workflow, Table View) & Right Actions (Filters Button) */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200">
        {/* Left Side: Tabs merging seamlessly with bottom border */}
        <div className="flex items-center -mb-px gap-2">
          <button
            type="button"
            onClick={() => setViewMode("kanban")}
            className={`relative flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium whitespace-nowrap transition-all duration-200 border-b-2 ${
              viewMode === "kanban"
                ? "border-blue-600 text-blue-600 font-semibold"
                : "border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300"
            }`}
          >
            <LayoutGrid size={15} className={viewMode === "kanban" ? "text-blue-600" : "text-slate-400"} />
            <span>Ticket Workflow</span>
          </button>
          <button
            type="button"
            onClick={() => setViewMode("list")}
            className={`relative flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium whitespace-nowrap transition-all duration-200 border-b-2 ${
              viewMode === "list"
                ? "border-blue-600 text-blue-600 font-semibold"
                : "border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300"
            }`}
          >
            <List size={15} className={viewMode === "list" ? "text-blue-600" : "text-slate-400"} />
            <span>Table View</span>
          </button>
        </div>

        {/* Right Side: Collapsible Filters Button */}
        <div className="flex items-center gap-2.5 py-1.5">
          {/* Collapsible Filters Toggle Button */}
          <button
            type="button"
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-semibold border transition-all shadow-2xs ${
              isFilterOpen || activeFiltersCount > 0
                ? "bg-blue-50/80 border-blue-200 text-blue-700"
                : "bg-white border-slate-200/90 text-slate-700 hover:bg-slate-50"
            }`}
          >
            <Filter size={13} className="text-blue-600" />
            <span>Filters</span>
            {activeFiltersCount > 0 && (
              <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#0066ff] text-white text-[10px] font-bold inline-grid place-items-center text-center tabular-nums leading-none shrink-0 shadow-xs">
                {activeFiltersCount}
              </span>
            )}
            {isFilterOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
        </div>
      </div>

      {/* Collapsible Filters Card */}
      {isFilterOpen && (
        <div className="bg-white rounded-2xl border border-slate-200/90 p-4 sm:p-5 shadow-xs space-y-4 transition-all">
          {/* Grid of Existing Filter Inputs with Header Labels */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3.5">
            {/* 1. Search */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5 h-4">
                <label className="text-xs font-semibold text-slate-700">Search</label>
                <Tooltip title="Search by ticket ID, subject, employee, category, or assignee." arrow placement="top">
                  <Info size={13} className="text-slate-400 hover:text-slate-600 cursor-pointer shrink-0" />
                </Tooltip>
              </div>
              <TextField
                size="small"  
                fullWidth
                placeholder="Search queries..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    fetchTickets();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 0.5 }}>
                      <Search size={14} className="text-slate-400" />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearch("")}
                        edge="end"
                        sx={{ p: 0.5 }}
                      >
                        <X size={13} className="text-slate-400 hover:text-slate-600" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 38,
                    borderRadius: "10px",
                    backgroundColor: "#ffffff",
                    fontSize: "0.75rem",
                    color: "#334155",
                    paddingLeft: "10px",
                    "& fieldset": { borderColor: "#e2e8f0" },
                    "&:hover fieldset": { borderColor: "#cbd5e1" },
                    "&.Mui-focused fieldset": { borderColor: "#3b82f6", borderWidth: "1.5px" },
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "8px 6px 8px 2px",
                    fontSize: "0.75rem",
                  },
                }}
              />
            </div>

            {/* 2. Raised By (Employee) */}
            <div className="space-y-1.5">
              <div className="flex items-center h-4">
                <label className="text-xs font-semibold text-slate-700">Raised By</label>
              </div>
              <Autocomplete
                multiple
                options={(employees || []).filter(
                  (el) => el.STATUS === "Active" && el.DSR_EXCEPTION !== "Yes"
                )}
                getOptionLabel={(option) =>
                  `${option.EMP_NAME} (${option.EMP_ID || option.EMP_CODE || ""})`
                }
                value={selectedRaisedEmployees}
                onChange={(event, newValue) => {
                  setSelectedRaisedEmployees(newValue);
                  fetchTickets({ selectedRaisedEmployees: newValue });
                }}
                size="small"
                className="w-full bg-white text-xs"
                limitTags={1}
                disableCloseOnSelect
                renderTags={() => null}
                isOptionEqualToValue={(option, value) => option.EMP_CODE === value.EMP_CODE}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 38,
                    borderRadius: "10px",
                    backgroundColor: "#ffffff",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: "#334155",
                    paddingTop: "0 !important",
                    paddingBottom: "0 !important",
                    "& fieldset": { borderColor: "#e2e8f0" },
                    "&:hover fieldset": { borderColor: "#cbd5e1" },
                    "&.Mui-focused fieldset": { borderColor: "#3b82f6", borderWidth: "1.5px" },
                  },
                  "& .MuiAutocomplete-input": {
                    paddingTop: "0 !important",
                    paddingBottom: "0 !important",
                    fontSize: "0.75rem",
                  },
                }}
                ListboxProps={{ sx: { fontSize: "0.75rem" } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      selectedRaisedEmployees.length > 0
                        ? `${selectedRaisedEmployees.length} Selected`
                        : "All Employees"
                    }
                  />
                )}
              />
            </div>

            {/* 3. Category */}
            <div className="space-y-1.5">
              <div className="flex items-center h-4">
                <label className="text-xs font-semibold text-slate-700">Category</label>
              </div>
              <FormControl size="small" fullWidth>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  displayEmpty
                  sx={{
                    height: 38,
                    borderRadius: "10px",
                    backgroundColor: "#ffffff",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: "#334155",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#3b82f6", borderWidth: "1.5px" },
                    "& .MuiSelect-select": { padding: "8px 12px" },
                  }}
                >
                  <MenuItem value="All" sx={{ fontSize: "0.75rem" }}>All Categories</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat} value={cat} sx={{ fontSize: "0.75rem" }}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            {/* 4. Status */}
            <div className="space-y-1.5">
              <div className="flex items-center h-4">
                <label className="text-xs font-semibold text-slate-700">Status</label>
              </div>
              <FormControl size="small" fullWidth>
                <Select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  displayEmpty
                  sx={{
                    height: 38,
                    borderRadius: "10px",
                    backgroundColor: "#ffffff",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: "#334155",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#3b82f6", borderWidth: "1.5px" },
                    "& .MuiSelect-select": { padding: "8px 12px" },
                  }}
                >
                  <MenuItem value="All" sx={{ fontSize: "0.75rem" }}>All Statuses</MenuItem>
                  <MenuItem value="New" sx={{ fontSize: "0.75rem" }}>New</MenuItem>
                  <MenuItem value="In Progress" sx={{ fontSize: "0.75rem" }}>In Progress</MenuItem>
                  <MenuItem value="Waiting for Employee" sx={{ fontSize: "0.75rem" }}>Waiting on Employee</MenuItem>
                  <MenuItem value="Resolved" sx={{ fontSize: "0.75rem" }}>Resolved</MenuItem>
                  <MenuItem value="Closed" sx={{ fontSize: "0.75rem" }}>Closed</MenuItem>
                </Select>
              </FormControl>
            </div>

            {/* 5. Priority */}
            <div className="space-y-1.5">
              <div className="flex items-center h-4">
                <label className="text-xs font-semibold text-slate-700">Priority</label>
              </div>
              <FormControl size="small" fullWidth>
                <Select
                  value={selectedPriority}
                  onChange={(e) => setSelectedPriority(e.target.value)}
                  displayEmpty
                  sx={{
                    height: 38,
                    borderRadius: "10px",
                    backgroundColor: "#ffffff",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: "#334155",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#3b82f6", borderWidth: "1.5px" },
                    "& .MuiSelect-select": { padding: "8px 12px" },
                  }}
                >
                  <MenuItem value="All" sx={{ fontSize: "0.75rem" }}>All Priorities</MenuItem>
                  <MenuItem value="High" sx={{ fontSize: "0.75rem" }}>High</MenuItem>
                  <MenuItem value="Medium" sx={{ fontSize: "0.75rem" }}>Medium</MenuItem>
                  <MenuItem value="Low" sx={{ fontSize: "0.75rem" }}>Low</MenuItem>
                </Select>
              </FormControl>
            </div>

            {/* 6. Assignee */}
            <div className="space-y-1.5">
              <div className="flex items-center h-4">
                <label className="text-xs font-semibold text-slate-700">Assignee</label>
              </div>
              <FormControl size="small" fullWidth>
                <Select
                  value={selectedAssignee}
                  onChange={(e) => setSelectedAssignee(e.target.value)}
                  displayEmpty
                  sx={{
                    height: 38,
                    borderRadius: "10px",
                    backgroundColor: "#ffffff",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: "#334155",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#3b82f6", borderWidth: "1.5px" },
                    "& .MuiSelect-select": { padding: "8px 12px" },
                  }}
                >
                  <MenuItem value="All" sx={{ fontSize: "0.75rem" }}>All Assignees</MenuItem>
                  {hrTeam.map((hr) => (
                    <MenuItem key={hr.empCode} value={hr.empCode} sx={{ fontSize: "0.75rem" }}>
                      {hr.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            {/* 7. Raised Date Button with Popover */}
            <div className="space-y-1.5">
              <div className="flex items-center h-4">
                <label className="text-xs font-semibold text-slate-700">Raised Date</label>
              </div>
              <button
                type="button"
                onClick={(e) => setDatePopoverAnchor(e.currentTarget)}
                className="w-full h-[38px] px-3 flex items-center justify-between text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-[10px] hover:border-slate-300 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-left shadow-2xs"
              >
                <div className="flex items-center gap-2 truncate">
                  <Calendar size={13} className="text-slate-400 shrink-0" />
                  <span className="truncate">{dateFilterLabel}</span>
                </div>
                <ChevronDown size={13} className="text-slate-400 shrink-0 ml-1" />
              </button>
            </div>
          </div>

          {/* Date Range Popover */}
          <Popover
            open={isDatePopoverOpen}
            anchorEl={datePopoverAnchor}
            onClose={() => {
              setDatePopoverAnchor(null);
              setIsCustomMode(false);
            }}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            PaperProps={{
              sx: {
                borderRadius: "14px",
                boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
                border: "1px solid #e2e8f0",
                mt: 1,
                overflow: "hidden",
              },
            }}
          >
            {!isCustomMode ? (
              <div className="p-2.5 w-52 space-y-0.5">
                <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400 px-2 py-1">
                  Raised Date
                </div>
                {[
                  { id: "all", label: "All Dates" },
                  { id: "today", label: "Today" },
                  { id: "this_week", label: "This Week" },
                  { id: "this_month", label: "This Month" },
                  { id: "last_month", label: "Last Month" },
                  { id: "custom", label: "Custom Range" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => handleSelectDateOption(opt.id)}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      dateOption === opt.id
                        ? "bg-blue-50 text-blue-700 font-semibold"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <span className="flex items-center gap-2.5">
                      <span
                        className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center transition-colors ${
                          dateOption === opt.id
                            ? "border-[#0066ff] bg-[#0066ff]"
                            : "border-slate-300 bg-white"
                        }`}
                      >
                        {dateOption === opt.id && (
                          <span className="w-1.5 h-1.5 rounded-full bg-white" />
                        )}
                      </span>
                      <span>{opt.label}</span>
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-4 w-72 space-y-3.5">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setIsCustomMode(false)}
                      className="text-slate-400 hover:text-slate-700 p-0.5 rounded text-xs font-bold"
                      title="Back to presets"
                    >
                      ←
                    </button>
                    <span className="text-xs font-bold text-slate-800">
                      Custom Raised Date
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setDatePopoverAnchor(null);
                      setIsCustomMode(false);
                    }}
                    className="text-slate-400 hover:text-slate-600 p-0.5 rounded"
                  >
                    <X size={14} />
                  </button>
                </div>

                <div className="space-y-2.5">
                  <div className="space-y-1">
                    <label className="text-[11px] font-semibold text-slate-600">
                      From
                    </label>
                    <input
                      type="date"
                      value={customRange.startDate}
                      onChange={(e) =>
                        setCustomRange((prev) => ({
                          ...prev,
                          startDate: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] font-semibold text-slate-600">
                      To
                    </label>
                    <input
                      type="date"
                      value={customRange.endDate}
                      onChange={(e) =>
                        setCustomRange((prev) => ({
                          ...prev,
                          endDate: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end pt-2 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={handleCustomDateDone}
                    className="px-4 py-1.5 text-xs font-semibold text-white bg-[#0066ff] hover:bg-[#0052cc] rounded-lg shadow-xs transition-colors"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </Popover>

          {/* Bottom Bar: Active Filter Badges on Left & Action Buttons (Reset, Apply) on Right */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2.5 border-t border-slate-100">
            {/* Active Badges */}
            <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
              {search.trim() && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 shadow-2xs">
                  <span>Search: "{search}"</span>
                  <button
                    type="button"
                    onClick={() => {
                      setSearch("");
                      fetchTickets({ search: "" });
                    }}
                    className="text-blue-500 hover:text-blue-800 transition-colors ml-0.5"
                  >
                    <X size={12} />
                  </button>
                </span>
              )}

              {selectedRaisedEmployees.length > 0 && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 shadow-2xs">
                  <span>
                    Raised By:{" "}
                    {selectedRaisedEmployees.length === 1
                      ? selectedRaisedEmployees[0].EMP_NAME
                      : `${selectedRaisedEmployees.length} Employees`}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedRaisedEmployees([]);
                      fetchTickets({ selectedRaisedEmployees: [] });
                    }}
                    className="text-blue-500 hover:text-blue-800 transition-colors ml-0.5"
                  >
                    <X size={12} />
                  </button>
                </span>
              )}

              {selectedCategory !== "All" && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 shadow-2xs">
                  <span>Category: {selectedCategory}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCategory("All");
                      fetchTickets({ selectedCategory: "All" });
                    }}
                    className="text-blue-500 hover:text-blue-800 transition-colors ml-0.5"
                  >
                    <X size={12} />
                  </button>
                </span>
              )}

              {selectedStatus !== "All" && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 shadow-2xs">
                  <span>Status: {selectedStatus}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedStatus("All");
                      fetchTickets({ selectedStatus: "All" });
                    }}
                    className="text-blue-500 hover:text-blue-800 transition-colors ml-0.5"
                  >
                    <X size={12} />
                  </button>
                </span>
              )}

              {selectedPriority !== "All" && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 shadow-2xs">
                  <span>Priority: {selectedPriority}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedPriority("All");
                      fetchTickets({ selectedPriority: "All" });
                    }}
                    className="text-blue-500 hover:text-blue-800 transition-colors ml-0.5"
                  >
                    <X size={12} />
                  </button>
                </span>
              )}

              {selectedAssignee !== "All" && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 shadow-2xs">
                  <span>
                    Assignee: {hrTeam.find((h) => h.empCode === selectedAssignee)?.name || selectedAssignee}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedAssignee("All");
                      fetchTickets({ selectedAssignee: "All" });
                    }}
                    className="text-blue-500 hover:text-blue-800 transition-colors ml-0.5"
                  >
                    <X size={12} />
                  </button>
                </span>
              )}

              {dateOption !== "all" && (dateRange.startDate || dateRange.endDate) && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 shadow-2xs">
                  <span>Raised Date: {dateFilterLabel}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setDateOption("all");
                      setDateRange({ startDate: null, endDate: null });
                      setIsCustomMode(false);
                      fetchTickets({ dateRange: { startDate: null, endDate: null } });
                    }}
                    className="text-blue-500 hover:text-blue-800 transition-colors ml-0.5"
                  >
                    <X size={12} />
                  </button>
                </span>
              )}
            </div>

            {/* Action Buttons: Reset & Apply Filters */}
            <div className="flex items-center gap-2.5 shrink-0 ml-auto">
              <button
                type="button"
                onClick={handleResetFilters}
                disabled={loading.fetch}
                className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-2xs"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={() => fetchTickets()}
                disabled={loading.fetch}
                className="inline-flex items-center gap-1.5 px-5 py-2 text-xs font-semibold text-white bg-[#0066ff] hover:bg-[#0052cc] disabled:opacity-50 disabled:cursor-not-allowed rounded-lg shadow-xs transition-colors"
              >
                {loading.fetch ? <LoaderIcon className="w-3.5 h-3.5" /> : null}
                <span>{loading.fetch ? "Applying..." : "Apply Filters"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VIEW 1: 4-Column Active Kanban Board with Dnd-Kit */}
      {viewMode === "kanban" && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
            {/* Column 1: New queries */}
            <DroppableKanbanColumn
              id="new"
              title="New Queries"
              count={kanbanColumns.new.length}
              icon={<FileText size={15} />}
              topBorderColor="border-t-[3.5px] border-t-slate-500"
              iconBg="bg-slate-100"
              iconColor="text-slate-600"
            >
              {kanbanColumns.new.map((ticket) => (
                <DraggableKanbanCard
                  key={ticket.REQUEST_ID}
                  ticket={ticket}
                  renderCard={renderKanbanCard}
                  onOpenTicket={handleOpenTicket}
                />
              ))}

              {kanbanColumns.new.length === 0 && (
                <div className="flex flex-col items-center justify-center pt-12 pb-8 text-center space-y-2">
                  <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-1">
                    <FileText size={22} />
                  </div>
                  <h4 className="text-xs font-bold text-slate-700">No new queries</h4>
                  <p className="text-[11px] text-slate-400">Newly raised tickets will appear here.</p>
                </div>
              )}
            </DroppableKanbanColumn>

            {/* Column 2: In Progress */}
            <DroppableKanbanColumn
              id="inProgress"
              title="In Progress"
              count={kanbanColumns.inProgress.length}
              icon={<Settings size={15} />}
              topBorderColor="border-t-[3.5px] border-t-blue-500"
              iconBg="bg-blue-50"
              iconColor="text-blue-600"
            >
              {kanbanColumns.inProgress.map((ticket) => (
                <DraggableKanbanCard
                  key={ticket.REQUEST_ID}
                  ticket={ticket}
                  renderCard={renderKanbanCard}
                  onOpenTicket={handleOpenTicket}
                />
              ))}

              {kanbanColumns.inProgress.length === 0 && (
                <div className="flex flex-col items-center justify-center pt-12 pb-8 text-center space-y-2">
                  <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-1">
                    <Settings size={22} />
                  </div>
                  <h4 className="text-xs font-bold text-slate-700">No tickets in progress</h4>
                  <p className="text-[11px] text-slate-400">Tickets under investigation will appear here.</p>
                </div>
              )}
            </DroppableKanbanColumn>

            {/* Column 3: Awaiting Employee */}
            <DroppableKanbanColumn
              id="waitingForEmployee"
              title="Awaiting Employee"
              count={kanbanColumns.waitingForEmployee.length}
              icon={<User size={15} />}
              topBorderColor="border-t-[3.5px] border-t-amber-500"
              iconBg="bg-amber-50"
              iconColor="text-amber-500"
            >
              {kanbanColumns.waitingForEmployee.map((ticket) => (
                <DraggableKanbanCard
                  key={ticket.REQUEST_ID}
                  ticket={ticket}
                  renderCard={renderKanbanCard}
                  onOpenTicket={handleOpenTicket}
                />
              ))}

              {kanbanColumns.waitingForEmployee.length === 0 && (
                <div className="flex flex-col items-center justify-center pt-12 pb-8 text-center space-y-2">
                  <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-1">
                    <User size={22} />
                  </div>
                  <h4 className="text-xs font-bold text-slate-700">No tickets waiting on employee</h4>
                  <p className="text-[11px] text-slate-400">Tickets under review will appear here.</p>
                </div>
              )}
            </DroppableKanbanColumn>

            {/* Column 4: Resolved */}
            <DroppableKanbanColumn
              id="resolved"
              title="Resolved"
              count={kanbanColumns.resolved.length}
              icon={<CheckCircle2 size={15} />}
              topBorderColor="border-t-[3.5px] border-t-emerald-500"
              iconBg="bg-emerald-50"
              iconColor="text-emerald-500"
            >
              {kanbanColumns.resolved.map((ticket) => (
                <DraggableKanbanCard
                  key={ticket.REQUEST_ID}
                  ticket={ticket}
                  renderCard={renderKanbanCard}
                  onOpenTicket={handleOpenTicket}
                />
              ))}

              {kanbanColumns.resolved.length === 0 && (
                <div className="flex flex-col items-center justify-center pt-12 pb-8 text-center space-y-2">
                  <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-1">
                    <CheckCircle2 size={22} />
                  </div>
                  <h4 className="text-xs font-bold text-slate-700">No resolved tickets</h4>
                  <p className="text-[11px] text-slate-400">Resolved tickets will appear here.</p>
                </div>
              )}
            </DroppableKanbanColumn>
          </div>

          {/* Floating Drag Overlay */}
          <DragOverlay
            dropAnimation={{
              duration: 200,
              easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
            }}
          >
            {activeTicket ? renderKanbanCard(activeTicket, true) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* VIEW 2: List View */}
      {viewMode === "list" && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden p-0">
          <CentralizedTable
            isLoading={loading.fetch}
            renderTopToolbarCustomActions={() => (
              <div className="flex items-center gap-3 py-2">
                <div className="p-2.5 rounded-xl bg-blue-50 text-blue-600">
                  <MessageSquare size={18} />
                </div>
                <div>
                  <h3 className="text-[15px] font-bold text-slate-800">
                    All Support Tickets ({tickets.length})
                  </h3>
                  <p className="text-xs text-slate-500">Track and manage all submitted queries and resolutions.</p>
                </div>
              </div>
            )}
            getRowId={(row) => row.REQUEST_ID}
            rows={tickets}
            columns={columns}
            rowHeight={52}
            columnHeaderHeight={44}
            autoHeight
            enableColumnPinning={true}
            muiTableHeadCellProps={({ column }) => ({
              sx: {
                boxShadow: "none !important",
                "&::before": {
                  boxShadow:
                    column.id === "actions"
                      ? "-4px 0 10px rgba(0,0,0,0.05) !important"
                      : "none !important",
                },
              },
            })}
            muiTableBodyCellProps={({ cell }) => ({
              sx: {
                py: "8px",
                borderBottom: "1px solid #e2e8f0",
                px: cell.column.getIndex() === 0 ? "16px" : "3px",
                overflow: cell.column.id === "actions" ? "visible" : "hidden",
                textOverflow: cell.column.id === "actions" ? "clip" : "ellipsis",
                backgroundColor:
                  cell.column.id === "actions" ? "#fff !important" : "inherit",
                boxShadow: "none !important",
                "&::before": {
                  boxShadow:
                    cell.column.id === "actions"
                      ? "-4px 0 10px rgba(0,0,0,0.05) !important"
                      : "none !important",
                },
              },
            })}
            initialState={{
              pagination: { paginationModel: { pageSize: 15, page: 0 } },
              columnPinning: { right: ["actions"] },
            }}
          />
        </div>
      )}

      {/* REDESIGNED 2-COLUMN UNIFIED TICKET UPDATE MODAL (Matching Reference Image) */}
      <TailwindModal
        isOpen={isDetailModalOpen}
        toggle={() => setIsDetailModalOpen(!isDetailModalOpen)}
        maxWidth="7xl"
      >
        {selectedTicket && (
          <div className="p-0 flex flex-col h-[86vh] max-h-[86vh] rounded-2xl bg-white overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="px-5 py-2.5 sm:px-6 sm:py-3 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
              <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
                {/* ID with Copy Button */}
                <div className="inline-flex items-center gap-1.5 bg-blue-50/80 border border-blue-200/80 text-blue-700 font-mono text-xs font-bold px-2 py-0.5 rounded-lg shadow-xs">
                  <span>#{selectedTicket.REQUEST_ID}</span>
                  <Tooltip title={isCopied ? "Copied!" : "Copy Ticket ID"}>
                    <button
                      onClick={handleCopyTicketId}
                      className="text-blue-500 hover:text-blue-800 transition-colors p-0.5 rounded"
                    >
                      {isCopied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
                    </button>
                  </Tooltip>
                </div>

                {/* Category Pill */}
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200/80">
                  {selectedTicket.CATEGORY || "General"}
                </span>

                {/* Priority Pill */}
                {renderPriorityBadge(selectedTicket.PRIORITY)}

                {/* Status Pill with User Icon */}
                {renderStatusBadge(selectedTicket.STATUS)}
              </div>

              {/* Close Button */}
              <button
                onClick={() => setIsDetailModalOpen(false)}
                className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors ml-2"
              >
                <X size={16} />
              </button>
            </div>

            {/* Modal Body (2 Columns with clean center divider & independent scrolling) */}
            <div className="flex-1 min-h-0 overflow-y-auto lg:overflow-hidden p-4 sm:p-5 bg-white">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-5 h-full min-h-0 items-stretch">
                {/* LEFT COLUMN: Support Query & Timeline Conversation History (lg:col-span-5) */}
                <div className="lg:col-span-5 flex flex-col h-full min-h-0 lg:pr-5 border-b lg:border-b-0 lg:border-r border-slate-200 pb-4 lg:pb-0 space-y-3">
                  {/* Top Support Query Card (Matching Image) */}
                  <div className="bg-[#f8faff] border border-slate-200/80 rounded-xl p-3 sm:p-3.5 space-y-1.5 shrink-0">
                    <h3 className="text-sm sm:text-base font-bold text-[#0f2d59]">
                      {selectedTicket.SUBJECT || "Support Query"}
                    </h3>
                    <div className="text-xs text-slate-400 font-medium flex flex-wrap items-center gap-1.5">
                      <span>
                        Raised by:{" "}
                        <strong className="text-slate-700 font-bold">
                          {selectedTicket.RAISED_BY} {selectedTicket.EMP_ID || selectedTicket.EMP_CODE ? `(${selectedTicket.EMP_ID || selectedTicket.EMP_CODE})` : ""}
                        </strong>
                      </span>
                      <span className="text-slate-300">|</span>
                      <span>{moment(selectedTicket.RAISED_DATE).format("DD MMM YYYY, hh:mm A")}</span>
                    </div>
                  </div>

                  {/* Auto-Close Schedule Notice for Resolved Tickets */}
                  {selectedTicket.STATUS === "Resolved" && (
                    <div className="bg-emerald-50/80 border border-emerald-200 rounded-xl p-3 space-y-2 shrink-0 text-xs shadow-xs">
                      <div className="flex items-center justify-between font-bold">
                        <span className="flex items-center gap-1.5 text-emerald-800">
                          <CheckCircle2 size={15} className="text-emerald-600" /> Ticket Resolved
                        </span>
                        <span className="text-[11px] text-emerald-700 font-medium">
                          {selectedTicket.RESOLVED_DATE ? moment(selectedTicket.RESOLVED_DATE).format("DD MMM YYYY, hh:mm A") : ""}
                        </span>
                      </div>
                      {selectedTicket.AUTO_CLOSE_DATE && (
                        <div className="flex items-center gap-1.5 text-[11px] text-amber-900 bg-amber-50/90 px-2.5 py-1.5 rounded-lg border border-amber-200">
                          <Clock size={13} className="text-amber-600 shrink-0" />
                          <span>
                            ⏰ <strong>Auto-Close Schedule:</strong> Auto-closing on <strong>{moment(selectedTicket.AUTO_CLOSE_DATE).format("DD MMM YYYY")}</strong> (2 business days review).
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Conversation History with dedicated separate scroll */}
                  <div className="flex-1 min-h-0 flex flex-col space-y-2">
                    <div className="flex items-center justify-between px-1 shrink-0">
                      <h4 className="text-xs sm:text-sm font-bold text-[#0f2d59]">
                        Conversation History
                      </h4>
                      <span className="text-xs text-slate-400 font-medium">
                        {(selectedTicket.messages || []).length} messages
                      </span>
                    </div>

                    {/* Scrollable Message Box with continuous connector line */}
                    <div
                      ref={messagesContainerRef}
                      className="flex-1 min-h-0 overflow-y-auto pr-1.5 pt-1 pb-1 space-y-3.5 custom-scrollbar"
                    >
                      {(selectedTicket.messages || []).map((msg, index, arr) => {
                        const isEmployee = msg.SENDER_ROLE === "Employee" || msg.TYPE === "EMP_QUERY";
                        const isAudit =
                          msg.TYPE &&
                          [
                            "STATUS_CHANGE",
                            "CLOSED",
                            "AUTO_CLOSED",
                            "ASSIGNMENT",
                            "PRIORITY_CHANGE",
                          ].includes(msg.TYPE);

                        if (isAudit) {
                          return (
                            <div
                              key={index}
                              className="relative flex items-center justify-center gap-2 py-0.5 text-xs text-slate-500 font-medium"
                            >
                              {index !== arr.length - 1 && (
                                <span
                                  className="absolute left-[15px] top-0 bottom-[-16px] w-[2px] bg-slate-100"
                                  aria-hidden="true"
                                />
                              )}
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-300 relative z-10" />
                              <span className="bg-slate-50 px-2.5 py-0.5 rounded-full text-[11px] text-slate-600 border border-slate-200 relative z-10">
                                {msg.MESSAGE}
                              </span>
                              <span className="text-[10px] text-slate-400 relative z-10">
                                ({moment(msg.RAISED_DATE).format("DD MMM, hh:mm A")})
                              </span>
                            </div>
                          );
                        }

                        const authorInitial = isEmployee
                          ? (msg.RAISED_BY || selectedTicket.RAISED_BY || "P").charAt(0).toUpperCase()
                          : "H";
                        const senderName = isEmployee
                          ? msg.RAISED_BY || selectedTicket.RAISED_BY || "POOARASU"
                          : "HR";

                        return (
                          <div key={index} className="relative flex items-start gap-3 group">
                            {/* Continuous connector line between messages */}
                            {index !== arr.length - 1 && (
                              <span
                                className="absolute left-[14px] top-[26px] bottom-[-20px] w-[2px] bg-slate-100"
                                aria-hidden="true"
                              />
                            )}

                            {/* Avatar Circle with Initial */}
                            <div
                              className={`relative z-10 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center shrink-0 font-bold text-xs shadow-xs ${
                                isEmployee
                                  ? "bg-[#a7f3d0] text-[#065f46]"
                                  : "bg-[#bfdbfe] text-[#1e40af]"
                              }`}
                            >
                              {authorInitial}
                            </div>

                            {/* Message Header & Bubble */}
                            <div className="flex-1 min-w-0 space-y-1">
                              {/* Header: Sender Name & Date */}
                              <div className="flex items-center gap-2 text-xs flex-wrap">
                                <span className="font-bold text-slate-800 uppercase tracking-wide text-[11px] sm:text-xs">
                                  {senderName}
                                </span>
                                {msg.TYPE === "RESOLVED" && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                                    <CheckCircle2 size={11} className="text-emerald-600" /> Resolution Note
                                  </span>
                                )}
                                {msg.TYPE === "REOPEN" && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                                    <RotateCcw size={11} className="text-amber-600" /> Reopened
                                  </span>
                                )}
                                <span className="text-[10px] sm:text-[11px] text-slate-400 font-normal ml-auto">
                                  {moment(msg.RAISED_DATE).format("DD MMM YYYY, hh:mm A")}
                                </span>
                              </div>

                              {/* Message Bubble Card */}
                              <div className="bg-[#f8fafc] border border-slate-100 rounded-xl px-3.5 py-2 sm:px-4 sm:py-2.5 text-xs text-slate-800 font-normal leading-relaxed">
                                <p className="whitespace-pre-wrap leading-relaxed">
                                  {msg.MESSAGE}
                                </p>

                                {/* Attachments */}
                                {msg.ATTACHMENTS && msg.ATTACHMENTS.length > 0 && (
                                  <div className="mt-2 pt-1.5 border-t border-slate-200/60 flex flex-wrap gap-1.5">
                                    {msg.ATTACHMENTS.map((file, fIdx) => (
                                      <button
                                        key={fIdx}
                                        type="button"
                                        onClick={(e) => handleDownloadAttachment(file, e)}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white hover:bg-blue-50 text-slate-700 hover:text-blue-700 rounded-lg text-[11px] font-semibold border border-slate-200 hover:border-blue-300 transition-all shadow-xs cursor-pointer group"
                                        title={`Download ${file.name || "file"}`}
                                      >
                                        <FileText size={12} className="text-blue-600 group-hover:text-blue-700" />
                                        <span className="truncate max-w-[140px]">{file.name || file.filename || "file"}</span>
                                        {file.size && <span className="text-[10px] text-slate-400">({file.size})</span>}
                                        <FileDown size={12} className="text-slate-400 group-hover:text-blue-600 ml-0.5" />
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {/* Auto scroll anchor */}
                      <div ref={messagesEndRef} />
                    </div>
                  </div>
                </div>

                {/* RIGHT COLUMN: Update Ticket Form (lg:col-span-7) */}
                <div className="lg:col-span-7 flex flex-col h-full min-h-0 overflow-y-auto custom-scrollbar pr-1 space-y-3">
                  <div className="space-y-0.5 shrink-0">
                    <h3 className="text-sm sm:text-base font-bold text-slate-800">Update Ticket</h3>
                    <p className="text-xs text-slate-500">
                      Update ticket details and message the employee.
                    </p>
                  </div>

                    {/* 3 Form Controls with Outer Labels in a Row (Priority, Status, Assignee) */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                      {/* 1. Priority Select */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">Priority</label>
                        <FormControl fullWidth size="small">
                          <Select
                            value={modalPriority}
                            onChange={(e) => setModalPriority(e.target.value)}
                            displayEmpty
                            className="rounded-xl text-xs font-semibold bg-white"
                            MenuProps={{
                              sx: { zIndex: 99999 },
                              PaperProps: {
                                sx: {
                                  zIndex: 99999,
                                  borderRadius: "0.75rem",
                                  boxShadow: "0 10px 25px -5px rgba(0,0,0,0.15)",
                                  border: "1px solid #e2e8f0",
                                },
                              },
                            }}
                            sx={{
                              borderRadius: "0.75rem",
                              fontSize: "0.8125rem",
                              "& .MuiSelect-select": { py: "7px" },
                            }}
                          >
                            <MenuItem value="Low">
                              <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-700">
                                <span className="w-2 h-2 rounded-full bg-blue-500" /> Low
                              </div>
                            </MenuItem>
                            <MenuItem value="Medium">
                              <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-700">
                                <span className="w-2 h-2 rounded-full bg-amber-500" /> Medium
                              </div>
                            </MenuItem>
                            <MenuItem value="High">
                              <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-700">
                                <span className="w-2 h-2 rounded-full bg-rose-600" /> High
                              </div>
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </div>

                      {/* 2. Status Select */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">Status</label>
                        <FormControl fullWidth size="small">
                          <Select
                            value={modalStatus}
                            onChange={(e) => {
                              setModalStatus(e.target.value);
                              if (e.target.value !== "Waiting for Employee" && e.target.value !== "Resolved") {
                                setMessageError(false);
                              }
                            }}
                            displayEmpty
                            className="rounded-xl text-xs font-semibold bg-white"
                            MenuProps={{
                              sx: { zIndex: 99999 },
                              PaperProps: {
                                sx: {
                                  zIndex: 99999,
                                  borderRadius: "0.75rem",
                                  boxShadow: "0 10px 25px -5px rgba(0,0,0,0.15)",
                                  border: "1px solid #e2e8f0",
                                },
                              },
                            }}
                            sx={{
                              borderRadius: "0.75rem",
                              fontSize: "0.8125rem",
                              "& .MuiSelect-select": { py: "7px" },
                            }}
                          >
                            <MenuItem value="New">New</MenuItem>
                            <MenuItem value="In Progress">In Progress</MenuItem>
                            <MenuItem value="Waiting for Employee">Waiting on Employee</MenuItem>
                            <MenuItem value="Resolved">Resolved</MenuItem>
                            {selectedTicket?.STATUS === "Closed" && (
                              <MenuItem value="Closed">Closed</MenuItem>
                            )}
                          </Select>
                        </FormControl>
                      </div>

                      {/* 3. Assignee Select */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">
                          Assignee <span className="text-rose-500">*</span>
                        </label>
                        <FormControl fullWidth size="small" error={assigneeError}>
                          <Select
                            value={modalAssigneeCode || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              const hr = hrTeam.find((h) => h.empCode === val);
                              setModalAssigneeCode(val);
                              setModalAssigneeName(hr?.name || "");
                              if (val && val.trim()) {
                                setAssigneeError(false);
                              }
                            }}
                            displayEmpty
                            className="rounded-xl text-xs font-semibold bg-white"
                            MenuProps={{
                              sx: { zIndex: 99999 },
                              PaperProps: {
                                sx: {
                                  zIndex: 99999,
                                  borderRadius: "0.75rem",
                                  boxShadow: "0 10px 25px -5px rgba(0,0,0,0.15)",
                                  border: "1px solid #e2e8f0",
                                  maxHeight: 280,
                                },
                              },
                            }}
                            sx={{
                              borderRadius: "0.75rem",
                              fontSize: "0.8125rem",
                              "& .MuiSelect-select": { py: "7px" },
                              ...(assigneeError
                                ? {
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#ef4444 !important",
                                      borderWidth: "1.5px !important",
                                    },
                                  }
                                : {}),
                            }}
                          >
                            <MenuItem value="">
                              <span className="text-slate-400 italic text-xs">Select Assignee</span>
                            </MenuItem>
                            {hrTeam.map((hr) => (
                              <MenuItem key={hr.empCode} value={hr.empCode}>
                                <div className="flex items-center gap-2 text-xs font-semibold text-slate-800">
                                  <Avatar
                                    sx={{ width: 18, height: 18, fontSize: 10, bgcolor: "#7c3aed" }}
                                  >
                                    {hr.name.charAt(0)}
                                  </Avatar>
                                  {hr.name}
                                </div>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {assigneeError && (
                          <span className="flex items-center gap-1 text-rose-500 text-[11px] mt-1 font-medium">
                            <AlertCircle size={12} /> Assignee is required
                          </span>
                        )}
                      </div>
                    </div>

                    {/* CLOSED TICKET BANNER */}
                    {selectedTicket?.STATUS === "Closed" && (
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 flex items-center justify-between gap-3">
                        <div className="flex items-start gap-2.5">
                          <CheckCircle2 size={16} className="text-slate-500 shrink-0 mt-0.5" />
                          <div className="space-y-0.5">
                            <span className="text-xs font-bold text-slate-700 block">This ticket is Closed (Read-Only)</span>
                            <span className="text-[11px] text-slate-500 font-normal block">To make changes or reply, reopen the ticket.</span>
                          </div>
                        </div>
                        {modalStatus === "Closed" && (
                          <button
                            type="button"
                            onClick={() => {
                              setModalStatus("In Progress");
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-blue-600 bg-white border border-blue-200 hover:bg-blue-50 rounded-lg shadow-2xs transition-colors shrink-0"
                          >
                            <RotateCcw size={12} />
                            <span>Reopen Ticket</span>
                          </button>
                        )}
                      </div>
                    )}

                    {/* DYNAMIC ALERT BOX BASED ON STATUS */}
                    {modalStatus === "Waiting for Employee" && selectedTicket?.STATUS !== "Waiting for Employee" && (
                      <div className="bg-[#ecf5fe] border border-[#d8e8ff] rounded-xl px-3.5 py-2.5 sm:px-4 sm:py-2.5 flex items-start gap-2.5">
                        <Info size={18} className="text-[#0164f1] shrink-0 mt-0.5" />
                        <div className="space-y-0.5">
                          <div className="text-xs font-bold text-[#0164f1]">
                            A message is required because the ticket is set to "Waiting on Employee".
                          </div>
                          <div className="text-xs text-slate-500 font-normal">
                            Please provide a clear message to the employee.
                          </div>
                        </div>
                      </div>
                    )}

                    {modalStatus === "Resolved" && selectedTicket?.STATUS !== "Resolved" && (
                      <div className="bg-[#ecfdf5] border border-[#a7f3d0] rounded-xl px-3.5 py-2.5 sm:px-4 sm:py-2.5 flex items-start gap-2.5">
                        <CheckCircle2 size={18} className="text-[#059669] shrink-0 mt-0.5" />
                        <div className="space-y-0.5">
                          <div className="text-xs font-bold text-[#065f46]">
                            A resolution note is required because the ticket is set to "Resolved".
                          </div>
                          <div className="text-xs text-slate-600 font-normal">
                            Explain the final resolution steps. The employee will have 2 business days to review before auto-closing on <strong className="text-emerald-800 font-semibold">{estimatedAutoCloseDate}</strong>.
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Message to Employee */}
                    <div className="space-y-1">
                      <label className="block text-xs font-bold text-slate-700">
                        Message to Employee {isMessageRequired ? <span className="text-rose-500">*</span> : <span className="text-slate-400 font-normal">(Optional)</span>}
                      </label>

                      {/* MUI TextField for Message */}
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder={
                          modalStatus === "Resolved"
                            ? "Provide the resolution details for the employee..."
                            : "Type your message to the employee..."
                        }
                        value={modalMessage}
                        onChange={(e) => {
                          setModalMessage(e.target.value);
                          if (e.target.value.trim() || modalAttachments.length > 0) {
                            setMessageError(false);
                          }
                        }}
                        error={messageError}
                        helperText={
                          messageError ? (
                            <span className="flex items-center gap-1 text-rose-500 text-xs mt-0.5 font-medium">
                              <AlertCircle size={12} /> Please fill message field
                            </span>
                          ) : ""
                        }
                        inputProps={{ maxLength: 2000 }}
                        className="bg-white"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0.75rem",
                            fontSize: "0.8125rem",
                            ...(messageError
                              ? {
                                  "& fieldset": { borderColor: "#ef4444 !important" },
                                }
                              : {}),
                          },
                        }}
                      />

                      {/* Character Counter */}
                      <div className="flex justify-end pt-0.5">
                        <span className="text-[11px] text-slate-400 font-normal">
                          {modalMessage.length}/2000
                        </span>
                      </div>
                    </div>

                    {/* Staged Attachments Preview */}
                    {modalAttachments.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-0.5 !mt-0">
                        {modalAttachments.map((att, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 text-slate-700 px-2.5 py-1 rounded-lg text-xs font-medium"
                          >
                            <FileText size={12} className="text-blue-600" />
                            <span className="max-w-[140px] truncate">{att.name}</span>
                            <button
                              type="button"
                              onClick={() => setModalAttachments((prev) => prev.filter((_, i) => i !== idx))}
                              className="text-slate-400 hover:text-rose-500 ml-1"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Attach File Button & Supported Formats Hint (Matching Image) */}
                    <div className="flex flex-wrap items-center gap-2.5 pt-0.5 !mt-2">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={loading.action}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-xs transition-colors"
                      >
                        <Paperclip size={13} className="text-slate-600" />
                        <span>Attach File</span>
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={handleFileUpload}
                      />
                      <span className="text-[11px] text-slate-400 font-normal">
                        Supported formats: PDF, DOC, DOCX, XLS, XLSX, PNG, JPG (Max 10 MB)
                      </span>
                    </div>
                </div>
              </div>
            </div>

            {/* Modal Footer: Cancel & Send Buttons (Matching Image) */}
            <div className="px-5 py-2.5 sm:px-6 sm:py-3 border-t border-slate-100 bg-white flex items-center justify-end gap-2.5 shrink-0">
              <button
                type="button"
                onClick={() => setIsDetailModalOpen(false)}
                disabled={loading.action}
                className="px-4 py-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 text-xs font-semibold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-xs"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendUnified}
                disabled={loading.action || !hasFormChanges}
                className="inline-flex items-center gap-1.5 px-5 py-2 bg-[#0066ff] hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#0066ff] text-white text-xs font-semibold rounded-xl shadow-xs transition-colors"
              >
                {loading.action ? (
                  <LoaderIcon className="w-3.5 h-3.5" />
                ) : (
                  <Send size={13} className="text-white" />
                )}
                <span>{loading.action ? "Saving..." : "Send"}</span>
              </button>
            </div>
          </div>
        )}
      </TailwindModal>
    </div>
  );
}